import React, { useState, useMemo } from "react";
import { Line } from "@reactchartjs/react-chart.js";
import { Box, IconButton, Typography } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { withStyles } from "@material-ui/styles";
import { compose } from "recompose";
import ApplicationUtil from "../../../utils/ApplicationUtil";

const WINDOW_SIZE = 6;

const dashedLineWithLabelsPlugin = {
  id: "dashedLineWithLabels",
  afterDatasetsDraw: (chart) => {
    const ctx = chart.ctx;
    if (
      !chart.data.datasets ||
      !chart.data.datasets[0] ||
      !chart.getDatasetMeta(0).data
    )
      return;

    const meta = chart.getDatasetMeta(0);
    const yAxis = chart.scales["y-axis-0"];

    meta.data.forEach((element, index) => {
      const value = chart.data.datasets[0].data[index];
      if (!element._model) return;

      const x = element._model.x;
      const y = element._model.y;

      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#3b82f6";
      ctx.moveTo(x, y);
      ctx.lineTo(x, yAxis.bottom);
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.fillStyle = "#1d78c1";
      ctx.font = "bold 12px Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText(`${value}%`, x + 5, y - 8);
      ctx.restore();
    });
  },
};

function WeeklyProgressChart({ classes, data }) {
  const [startIndex, setStartIndex] = useState(0);

  const chartItems = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.weeklyPercentile)) {
      return data.weeklyPercentile;
    }
    return [];
  }, [data]);

  const visibleData = useMemo(() => {
    if (chartItems.length === 0) return [];
    return chartItems.slice(startIndex, startIndex + WINDOW_SIZE);
  }, [chartItems, startIndex]);

  const handlePrev = () => {
    setStartIndex((prev) => Math.max(0, prev - WINDOW_SIZE));
  };

  const handleNext = () => {
    if (startIndex + WINDOW_SIZE < chartItems.length) {
      setStartIndex((prev) => prev + WINDOW_SIZE);
    }
  };

  const isMobile = window.innerWidth < 768;

  // --- Chart Configuration ---
  const chartData = {
    labels: visibleData.map((d) => {
      const weekLabel = `Week ${d.weekNumber}`;
      if (isMobile) {
        return weekLabel;
      }
      const dateRange = `${ApplicationUtil.formatDate(d.weekStartDate)} - ${ApplicationUtil.formatDate(d.weekEndDate)}`;
      return [weekLabel, dateRange];
    }),
    datasets: [
      {
        label: "Percentile Rank",
        data: visibleData.map((d) => d.percentileRank),
        fill: false,
        borderColor: "#1d78c1",
        backgroundColor: "#1d78c1",
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: "#1d78c1",
        lineTension: 0,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 30, left: 10, right: 10, bottom: 0 },
    },
    legend: { display: false },
    tooltips: {
      enabled: true,
      mode: "index",
      intersect: false,
      callbacks: {
        label: function (tooltipItem) {
          return `Rank: ${tooltipItem.yLabel}%`;
        },
      },
    },
    scales: {
      yAxes: [
        {
          id: "y-axis-0",
          ticks: {
            beginAtZero: true,
            max: 100,
            stepSize: 10,
            fontColor: "#9ca3af",
            padding: 20,
          },
          gridLines: {
            color: "#e5e7eb",
            drawBorder: false,
          },
        },
      ],
      xAxes: [
        {
          id: "x-axis-0",
          gridLines: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            fontColor: "#000",
            fontStyle: "bold",
            padding: 20,
            min: chartData.labels[0],
          },
          // This property helps distribute points evenly
          offset: true,
        },
      ],
    },
  };

  if (chartItems.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography color="textSecondary" align="center">
          No data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <IconButton
        onClick={handlePrev}
        disabled={startIndex === 0}
        size="small"
        className={classes.navButton}
      >
        <ArrowBackIosIcon style={{ fontSize: 16 }} />
      </IconButton>

      <Box className={classes.chartWrapper}>
        <Line
          data={chartData}
          options={options}
          plugins={[dashedLineWithLabelsPlugin]}
        />
      </Box>

      <IconButton
        onClick={handleNext}
        disabled={startIndex + WINDOW_SIZE >= chartItems.length}
        size="small"
        className={classes.navButton}
      >
        <ArrowForwardIosIcon style={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

const styles = (theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "10px 0",
  },
  navButton: {
    fontSize: "16px",
    margin: "0 5px",
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#333 !important",
    },
  },
  chartWrapper: {
    flexGrow: 1,
    position: "relative",
    height: "300px",
  },
});

export default compose(withStyles(styles, { withTheme: true }))(
  WeeklyProgressChart,
);
